import config from "../config";
import { WPM, MONTHLY, WEEKLY } from '../vars';

const DEFAULT_TIME_ZONE = "Australia/Sydney";


export const isRefinanceLoanOptionsJourney = (journey) => journey?.type === 'Refinance'
  && ['Personal', 'SMSF'].includes(journey.ownerType);

export const addCommas = number => {
  const originString = number + '';
  const numArray = originString.split('.');
  let intPart = numArray[0];
  const fractionPart = numArray.length > 1 ? '.' + numArray[1] : '';
  const regExp = /(\d+)(\d{3})/;
  while (regExp.test(intPart)) {
    intPart = intPart.replace(regExp, '$1,$2');
  }
  return intPart + fractionPart;
};

export const removeCommas = strNumber => {
  return parseFloat(strNumber.replace(/,/g, ''));
};

export const formatDollarAmountMonthly = (value, abs = false) => {
  const coefficient = value?.frequency === MONTHLY ? 1 : value?.frequency === WEEKLY ? WPM : 1 / 12;
  return `$${addCommas(((abs ? Math.abs(Number(value?.amount ?? 0)) : Number(value?.amount ?? 0)) * coefficient).toFixed(2))}`;
};

export const formatDollarAmountYearly = (value, abs = false) => {
  const coefficient = value?.frequency === MONTHLY ? 12 : value?.frequency === WEEKLY ? 12 * WPM : 1;
  return `$${addCommas(((abs ? Math.abs(Number(value?.amount ?? 0)) : Number(value?.amount ?? 0)) * coefficient).toFixed(2))}`;
};

export const dollarAmountMonthly = (value) => {
  const coefficient = value?.frequency === MONTHLY ? 1 : value?.frequency === WEEKLY ? WPM : 1 / 12;
  return Number(value?.amount ?? 0) * coefficient;
};

export const base64ToBlob = (base64String) => {
  const splitDataURI = base64String.split(',');
  const byteString = splitDataURI[0].indexOf('base64') >= 0 ? window.atob(splitDataURI[1]) : decodeURI(splitDataURI[1]);
  const mimeString = splitDataURI[0].split(':')[1].split(';')[0];

  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++)
    ia[i] = byteString.charCodeAt(i);

  return new Blob([ia], { type: mimeString });
};

export const setVerticalLineRenderParameters = (refs, setLineParameters) => {
  const activeRefs = refs
    .filter((ref) => !!ref?.current && !isNaN(ref.current.offsetLeft) && !isNaN(ref.current.offsetTop));
  let lineParameters = { left: undefined, top: undefined, height: undefined };
  if (activeRefs.length > 1) {
    const left = activeRefs[0].current?.offsetLeft;
    const tops = activeRefs.map((ref) => ref.current?.offsetTop);
    lineParameters = {
      left: `${left + 6}px`,
      top: `${Math.min(...tops) + 6}px`,
      height: `${Math.max(...tops) - Math.min(...tops)}px`
    };
  }

  setLineParameters(current => JSON.stringify(lineParameters) !== JSON.stringify(current) ? lineParameters : current);
};

export const getFileTypeObject = (fileName) => {
  const extension = (fileName ?? '').split('.').reverse()[0];
  switch (extension) {
    case 'png':
      return { type: 'image/png' };
    case 'jpg':
    case 'jpeg':
      return { type: 'image/jpeg' };
    case 'bmp':
      return { type: 'image/bmp' };
    case 'gif':
      return { type: 'image/gif' };
    case 'pdf':
      return { type: 'application/pdf' };
    case 'doc':
      return { type: 'application/msword' };
    case 'docx':
      return { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' };
    case 'rtf':
      return { type: 'application/rtf' };
    case 'txt':
      return { type: 'text/plain' };
    default:
      return {};
  }
};

export const decodeHTMLEntities = (function() {
  const element = document.createElement('div');

  function decodeHTMLEntities (str) {
    if(str && typeof str === 'string') {
      str = str.replace(/<script[^>]*>([\S\s]*?)<\/script>/gmi, '');
      str = str.replace(/<\/?\w(?:[^"'>]|"[^"]*"|'[^']*')*>/gmi, '');
      element.innerHTML = str;
      str = element.textContent;
      element.textContent = '';
    }

    return str;
  }

  return decodeHTMLEntities;
})();

export const getFileExtension = (file) => {
  const substrings = (file?.originalName ?? '').split('.');
  return substrings?.[(substrings?.length ?? 1) - 1] ?? '';
};

export const isFilePreviewSupported = (file) => {
  return ['jpg', 'jpeg', 'png', 'pdf', 'bmp', 'gif'].includes(getFileExtension(file));
};

export const isUrgentWithSignedContract = (journey) => {
  let userNeeds = journey?.userNeeds;

  return userNeeds?.whereAreYouInJourney === 'Urgent home loan'
    && !!userNeeds.conveyancerNeeds?.contractSigned
    && userNeeds.conveyancerNeeds.settlementDate;
};

export const isUrgentWithNoSignedContract = (journey) => {
  let userNeeds = journey?.userNeeds;

  return userNeeds?.whereAreYouInJourney === 'Urgent home loan'
    && (
      !userNeeds.conveyancerNeeds?.contractSigned || !userNeeds.conveyancerNeeds.settlementDate
    );
};

export const clearJourneysDashboardFilters = () => {
  localStorage.removeItem('SPECIALIST-JOURNEY-SEARCH-STRING');
  localStorage.removeItem('SPECIALIST-CHOSEN-USER-FILTER');
  localStorage.removeItem('SPECIALIST-CHOSEN-MILESTONE-FILTER');
  localStorage.removeItem('SPECIALIST-CHOSEN-SPECIALIST-FILTER-JOURNEYS');
  localStorage.removeItem('SPECIALIST-CHOSEN-ACCOUNT-FILTER-JOURNEYS');
  localStorage.removeItem('SPECIALIST-CHOSEN-REFERENCE-FILTER-JOURNEYS');
};

export const clearCustomersDashboardFilters = () => {
  localStorage.removeItem('SPECIALIST-CUSTOMER-SEARCH-STRING');
  localStorage.removeItem('SPECIALIST-CHOSEN-IDENTITY-FILTER');
  localStorage.removeItem('SPECIALIST-CHOSEN-STATUS-FILTER');
  localStorage.removeItem('SPECIALIST-CHOSEN-SPECIALIST-FILTER-CUSTOMERS');
  localStorage.removeItem('SPECIALIST-CHOSEN-ACCOUNT-FILTER-CUSTOMERS');
  localStorage.removeItem('SPECIALIST-CHOSEN-REFERENCE-FILTER-CUSTOMERS');
};

export const clearActionsDashboardFilters = () => {
  localStorage.removeItem('ACTIONS-SPECIALIST-ACTION-SEARCH-STRING');
  localStorage.removeItem('ACTIONS-SPECIALIST-CHOSEN-OWNER-FILTER');
  localStorage.removeItem('ACTIONS-SPECIALIST-CHOSEN-MILESTONE-FILTER');
  localStorage.removeItem('ACTIONS-SPECIALIST-CHOSEN-SPECIALIST-FILTER');
  localStorage.removeItem('ACTIONS-SPECIALIST-CHOSEN-ASSIGNEE-FILTER');
  localStorage.removeItem('ACTIONS-SPECIALIST-CHOSEN-STATUSES-FILTER');
  localStorage.removeItem('ACTIONS-SPECIALIST-CHOSEN-TYPE-FILTER');
  localStorage.removeItem('ACTIONS-SPECIALIST-SELECTED-TAB');
};

export const clearTimeTrackingInLocalStorage = () => {
  localStorage.removeItem('timeTrackingAction');
  localStorage.removeItem('timeTrackingActionName');
  localStorage.removeItem('timeTrackingUser');
  localStorage.removeItem('timeTrackingSeconds');
  localStorage.removeItem('timeTrackingLastUpdate');
  localStorage.removeItem('timeTrackingJourney');
  localStorage.removeItem('timeTrackingCustomer');
};

export const processActionTimeTrackerTick = (user) => {
  const prevUpdateStr = localStorage.getItem('timeTrackingLastUpdate');
  const prevUpdate = prevUpdateStr && !isNaN(Number(prevUpdateStr)) ? Number(prevUpdateStr) : undefined;

  if (prevUpdate) {
    const currentMilliseconds = Date.now();
    const elapsedTimeSec = (currentMilliseconds - prevUpdate) / 1000;

    if (elapsedTimeSec > config.TIME_LOGGER_RESET_TIME_SECONDS) {
      clearTimeTrackingInLocalStorage();
    } else {
      if (user?.id && user.id === localStorage.getItem('timeTrackingUser') && elapsedTimeSec > 0) {
        const prevSeconds = Number(localStorage.getItem('timeTrackingSeconds') ?? '0');
        // Use real elapsed wall-clock time so background-tab throttling does not undercount tracked time.
        const newTimeSeconds = prevSeconds + elapsedTimeSec;
        localStorage.setItem('timeTrackingLastUpdate', currentMilliseconds.toString());
        localStorage.setItem('timeTrackingSeconds', newTimeSeconds.toString());
      }
    }
  }
};

export const setRowsPerPageLimit = (rowsPerPage, setNewLimitFn) => {
  const LIMIT_OPTIONS = [10, 50, 100];

  if (isNaN(rowsPerPage) || !LIMIT_OPTIONS.includes(rowsPerPage)) {
    let rppCookie = localStorage.getItem('ROWS-PER-PAGE');
    if (!isNaN(rppCookie) && rppCookie !== '0' && LIMIT_OPTIONS.includes(Number(rppCookie))) {
      setNewLimitFn(Number(rppCookie));
    } else {
      setNewLimitFn(LIMIT_OPTIONS[0]);
      localStorage.removeItem('ROWS-PER-PAGE');
    }
  }
};

export const convertToKandM = (value) => {
  const number = Number(value);
  if (isNaN(number)) return number;
  if (Math.abs(number) < 1e3) return number.toFixed(2);
  if (Math.abs(number) >= 1e3 && number < 1e6) return (number / 1e3).toFixed(1) + 'K';
  if (Math.abs(number) >= 1e6 && number < 1e9) return (number / 1e6).toFixed(1) + 'M';
  if (Math.abs(number) >= 1e9 && number < 1e12) return (number / 1e9).toFixed(1) + 'B';
  if (Math.abs(number) >= 1e12) return (number / 1e12).toFixed(1) + 'T';
};

export const getDiscountDescription = (discounts) => {
  let description = '';

  (discounts ?? []).forEach((discount, idx) => {
    if (!!discount.description?.length) description += `Fee waiver: ${discount.description}\n\n`;

    if (discount.eligibility?.length && (discount.eligibility ?? []).some((e) => e.length)) {
      description += 'Fee waiver conditions: ';
      (discount.eligibility).forEach((e) => {
        description += `${e} `;
      });
      description += idx < ((discounts ?? []).length - 1) ? '\n\n' : '';
    }
  });

  return description;
};

export const getTimeZone = () => {
  const timezoneOffset = new Date().getTimezoneOffset()
  const offset = Math.abs(timezoneOffset)
  const offsetOperator = timezoneOffset < 0 ? '+' : '-'
  const offsetHours = Math.floor(offset / 60).toString().padStart(2, '0')
  const offsetMinutes = Math.floor(offset % 60).toString().padStart(2, '0')

  return `${offsetOperator}${offsetHours}:${offsetMinutes}`
};

export const isDateValid = (date) => {
  if (Object.prototype.toString.call(date) === "[object Date]") {
    if (isNaN(date)) {
      // date object is not valid
      return false;
    } else {
      // date object is valid
      return true;
    }
  } else {
    // not a date object
    return false;
  }
};

export const formatMobileString = (mobile) => {
  if (!mobile || mobile.length < 9 || mobile.length > 10) return '';

  let formatted = `+61${mobile.substr(0, 1) === '0' ? mobile.substr(1, 9) : mobile}`.split('');
  formatted.splice(9, 0, '\xa0');
  formatted.splice(6, 0, '\xa0');
  formatted.splice(3, 0, '\xa0');
  formatted = formatted.join('');

  return formatted;
};

export const isFeatureEnabled = (featureFlags, feature) => {
  let featureFlag = (featureFlags ?? []).find((ff) => ff.flagUID === feature);

  return featureFlag?.enabled ?? false;
};

export const saveSvg = (svgEl, name) => {
  svgEl.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  let svgData = svgEl.outerHTML;
  let preface = '<?xml version="1.0" standalone="no"?>\r\n';
  let svgBlob = new Blob([preface, svgData], {type:"image/svg+xml;charset=utf-8"});
  let svgUrl = URL.createObjectURL(svgBlob);
  let downloadLink = document.createElement("a");
  downloadLink.href = svgUrl;
  downloadLink.download = name;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
}

export const getConveyancingDiscountType = (journey, configParameters, discountEstimation) => {
  let price = journey?.conveyancing?.price ?? configParameters?.conveyancingPrice ?? 0;
  let discount = journey?.conveyancing?.discount ?? discountEstimation?.discount ?? 0;

  if (price < 0.1 || discount > 99.9) return 'Complimentary';
  else if (discount >= 0.1) return 'With discount';
  else return 'Full price';
};

// Cache for memoizing elapsed time calculations
const elapsedTimeCache = new Map();
const CACHE_TTL_MS = 60000; // 1 minute cache lifetime

/**
 * Calculate elapsed business time in minutes from startDate to current time
 * Excludes weekends and non-business hours
 *
 * @param {Date} startDate - The start date
 * @param {Object} businessHours - Business hours configuration
 * @param {string} businessHours.start - Start time in 'HH:MM' format (e.g., '9:00')
 * @param {string} businessHours.finish - Finish time in 'HH:MM' format (e.g., '17:00')
 * @param {string} businessHours.timeZone - IANA timezone identifier (e.g., 'Australia/Sydney' for AEST)
 * @returns {number} Elapsed business time in minutes, or 0 if startDate is in the future
 */
export const getElapsedTimeMinutes = (startDate, businessHours) => {
  if (!startDate || !businessHours) return 0;

  const now = new Date();
  const nowTime = now.getTime();

  // Return 0 if start date is in the future
  if (startDate > now) return 0;

  // Create cache key based on start date (to minute precision) and current minute
  const startTimestamp = Math.floor(startDate.getTime() / 60000) * 60000;
  const currentMinute = Math.floor(nowTime / 60000) * 60000;
  const cacheKey = `${startTimestamp}_${currentMinute}_${businessHours.timeZone}`;

  // Check cache first
  const cached = elapsedTimeCache.get(cacheKey);
  if (cached && (nowTime - cached.timestamp) < CACHE_TTL_MS) {
    return cached.value;
  }

  // Parse business hours
  const [startHour, startMinute] = businessHours.start.split(':').map(Number);
  const [finishHour, finishMinute] = businessHours.finish.split(':').map(Number);
  const businessStartMinutes = startHour * 60 + startMinute;
  const businessFinishMinutes = finishHour * 60 + finishMinute;
  const businessDayMinutes = businessFinishMinutes - businessStartMinutes;

  // Convert timezone abbreviation to IANA format
  const timezoneMap = {
    'AEST': 'Australia/Sydney',
    'AEDT': 'Australia/Sydney',
    'ACST': 'Australia/Adelaide',
    'ACDT': 'Australia/Adelaide',
    'AWST': 'Australia/Perth',
  };
  const timeZone = timezoneMap[businessHours.timeZone] || businessHours.timeZone;

  /**
   * Check if a date is a weekend in the specified timezone
   */
  const isWeekend = (date) => {
    const dateStr = date.toLocaleString('en-US', { timeZone, weekday: 'short' });
    return dateStr === 'Sat' || dateStr === 'Sun';
  };

  let totalMinutes = 0;

  // Create date objects in the target timezone
  const startInTz = new Date(startDate.toLocaleString('en-US', { timeZone }));
  const nowInTz = new Date(now.toLocaleString('en-US', { timeZone }));

  // Reset time to start of day for iteration
  const currentDay = new Date(startInTz);
  currentDay.setHours(0, 0, 0, 0);

  const endDay = new Date(nowInTz);
  endDay.setHours(0, 0, 0, 0);

  // If same day
  if (currentDay.getTime() === endDay.getTime()) {
    if (!isWeekend(startDate)) {
      const startMinutes = startInTz.getHours() * 60 + startInTz.getMinutes();
      const endMinutes = nowInTz.getHours() * 60 + nowInTz.getMinutes();

      const effectiveStart = Math.max(businessStartMinutes, startMinutes);
      const effectiveEnd = Math.min(businessFinishMinutes, endMinutes);

      totalMinutes = Math.max(0, effectiveEnd - effectiveStart);
    }
  } else {
    // Calculate minutes for the start date
    if (!isWeekend(startDate)) {
      const startMinutes = startInTz.getHours() * 60 + startInTz.getMinutes();
      const effectiveStart = Math.max(businessStartMinutes, startMinutes);
      totalMinutes += Math.max(0, businessFinishMinutes - effectiveStart);
    }

    // Iterate through middle days
    currentDay.setDate(currentDay.getDate() + 1);

    while (currentDay < endDay) {
      const checkDate = new Date(currentDay);
      if (!isWeekend(checkDate)) {
        totalMinutes += businessDayMinutes;
      }
      currentDay.setDate(currentDay.getDate() + 1);
    }

    // Calculate minutes for the end date
    if (!isWeekend(now)) {
      const endMinutes = nowInTz.getHours() * 60 + nowInTz.getMinutes();
      const effectiveEnd = Math.min(businessFinishMinutes, endMinutes);
      totalMinutes += Math.max(0, effectiveEnd - businessStartMinutes);
    }
  }

  const result = Math.floor(totalMinutes);

  // Store in cache and clean old entries periodically
  elapsedTimeCache.set(cacheKey, { value: result, timestamp: nowTime });

  // Clean cache if it gets too large (keep last 1000 entries)
  if (elapsedTimeCache.size > 1000) {
    const entriesToDelete = Array.from(elapsedTimeCache.entries())
      .sort((a, b) => a[1].timestamp - b[1].timestamp)
      .slice(0, elapsedTimeCache.size - 1000)
      .map(([key]) => key);

    entriesToDelete.forEach(key => elapsedTimeCache.delete(key));
  }

  return result;
};

export const convertTimeToLocalHours = (timeStr, sourceTimeZone) => {
  const [hStr, mStr = "00"] = timeStr.split(":");
  const hour = Number(hStr);
  const minute = Number(mStr || "0");
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return '';
  }
  let formatted;

  if (sourceTimeZone) {
    const timezoneMap = {
      'AEST': 'Australia/Sydney',
      'AEDT': 'Australia/Sydney',
      'ACST': 'Australia/Adelaide',
      'ACDT': 'Australia/Adelaide',
      'AWST': 'Australia/Perth',
    };
    const formattedSourceTimeZone = timezoneMap[sourceTimeZone] || sourceTimeZone;

    const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    function zonedWallTimeToDate(y, m, d, hh, mm, timeZone) {
      const utcGuess = Date.UTC(y, m - 1, d, hh, mm, 0, 0);
      const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
      const parts = Object.fromEntries(fmt.formatToParts(utcGuess).map(p => [p.type, p.value]));
      const asIfUTC = Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour),
        Number(parts.minute),
        Number(parts.second)
      );
      const offset = asIfUTC - utcGuess;
      return new Date(utcGuess - offset);
    }

    const todayPartsInSource = (() => {
      const now = Date.now();
      const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: formattedSourceTimeZone,
        year: 'numeric', month: '2-digit', day: '2-digit'
      });
      const parts = Object.fromEntries(fmt.formatToParts(now).map(p => [p.type, p.value]));
      return {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
      };
    })();

    const sourceInstant = zonedWallTimeToDate(
      todayPartsInSource.year,
      todayPartsInSource.month,
      todayPartsInSource.day,
      hour,
      minute,
      formattedSourceTimeZone
    );

    formatted = new Intl.DateTimeFormat('en-US', {
      timeZone: localZone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(sourceInstant);
  } else {
    //With no time zone conversion
    const now = new Date();
    let sourceDate = new Date(
      new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(now)
    );
    sourceDate.setHours(hour, minute, 0, 0);
    const options = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    formatted = sourceDate.toLocaleTimeString('en-US', options);
  }

  return formatted.replace(':00', '');
}

export const applyTimeForZone = (date, hours, minutes, seconds, milliseconds, timeZone = DEFAULT_TIME_ZONE) => {
  function setTimeInTimeZone({ year, month, day, hours, minutes, seconds, timeZone }) {
    const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));

    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });

    const parts = formatter.formatToParts(utcDate);

    const map = Object.fromEntries(parts.map(p => [p.type, p.value]));

    return new Date(Date.UTC(
      map.year,
      map.month - 1,
      map.day,
      map.hour,
      map.minute,
      map.second,
      milliseconds
    ));
  }

  return setTimeInTimeZone({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hours,
    minutes,
    seconds,
    timeZone,
  });
}

export const setTimezoneDate = (date, displacementInDays = 0, timeZone = DEFAULT_TIME_ZONE) => {
  let result = new Date(date);
  result.setDate(result.getDate() + displacementInDays);
  return new Date(applyTimeForZone(result,0,0,0,0, timeZone));
};

export const isHtmlEmpty = (htmlString) => {
  if (!htmlString.trim()) return true;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const body = doc.body;

  const allowedTags = new Set(["P", "B", "I", "BR", "SPAN", "DIV"]);

  function checkNode(node) {
    switch (node.nodeType) {
      case Node.TEXT_NODE:
        return !node.nodeValue || !node.nodeValue.trim();
      case Node.ELEMENT_NODE:
        if (!allowedTags.has(node.tagName)) return false;
        for (const child of node.childNodes) {
          if (!checkNode(child)) return false;
        }
        return true;
      case Node.COMMENT_NODE:
        return true;
      default:
        return false;
    }
  }

  for (const child of body.childNodes) {
    if (!checkNode(child)) return false;
  }

  return true;
}

export const setZeroUTCHours = (date) => {
  const d = new Date(date);
  const localMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const offsetMs = localMidnight.getTimezoneOffset() * 60 * 1000;
  return new Date(localMidnight.getTime() - offsetMs);
};

export const setUTCDate = (date, displacementInDays = 0) => {
  let result = new Date(date);
  result.setDate(result.getDate() + displacementInDays);
  return setZeroUTCHours(result);
};

const getCurrentJourneyConveyancingStep = (journey) => (
  (journey?.conveyancingSteps ?? []).find((item) => item.status === 'Current' && item.step?.category === 'Conveyancer')
);
export const getCurrentConveyancingStepUid = (journey) => getCurrentJourneyConveyancingStep(journey)?.step?.uid;
const getCurrentJourneySpecialistStep = (journey) => (
  (journey?.steps ?? []).find((item) => item.status === 'Current' && item.step?.category === 'Specialist')
);
export const getCurrentSpecialistStepUid = (journey) => getCurrentJourneySpecialistStep(journey)?.step?.uid;

//it is not taken from journey.conveyancing.signatures directly, as the journey may be rolled back to pre-signed state with saved journey.conveyancing.signatures
export const isConveyancingAgreementSigned = (journey) => {
  const currentConveyancerStepUid = getCurrentConveyancingStepUid(journey);
  return [
    'contract_negotiation',
    'contract_exchange_in_progress',
    'loan_prepared_for_settlement',
    'waiting_for_additional_information',
    'waiting_for_secondary_applicant_information',
    'ready_for_settlement',
    'conveyancing_settlement_completed',

    'sale_upload_documents',
    'sale_resume_upload_documents',
    'sale_preparing_contract_of_sale',
    'sale_waiting_for_additional_information',
    'sale_contract_of_sale_ready',
    'sale_contract_exchange_in_progress',
    'sale_loan_prepared_for_settlement',
    'sale_settlement_waiting_for_additional_information',
    'sale_settlement_waiting_for_secondary_applicant_information',
    'sale_ready_for_settlement',
    'sale_settlement_completed'
  ].includes(currentConveyancerStepUid);
};

export const isConveyancingSignedButNotReadyForSettlement = (journey) => {
  const currentConveyancerStepUid = getCurrentConveyancingStepUid(journey);
  return [
    'contract_negotiation',
    'contract_exchange_in_progress',
    'loan_prepared_for_settlement',
    'waiting_for_additional_information',
    'waiting_for_secondary_applicant_information'
  ].includes(currentConveyancerStepUid);
};

export const isAssessmentSignedButNotReadyForSettlement = (journey) => {
  const currentSpecialistStepUid = getCurrentSpecialistStepUid(journey);
  return [
    'assessment_complete',
    'pre_approval_review',
    'pre_approval_upload_documents',
    'pre_approval_upload_documents_secondary',
    'pre_approval_submission_review',
    'pre_approval_submission_upload_documents',
    'pre_approval_submission_upload_documents_secondary',
    'pre_approval_submission_finalised',
    'formal_approval_resume',
    'formal_approval_waiting_for_contract_exchange',
    'formal_approval_review',
    'formal_approval_upload_documents',
    'formal_approval_upload_documents_secondary',
    'formal_approval_submitted',
    'formal_approval_submission_review',
    'formal_approval_submission_upload_documents',
    'formal_approval_submission_upload_documents_secondary',
    'formal_approval_submission_finalised',
    'formal_approval_loan_documents',
    'formal_approval_loan_documents_upload_documents',
    'formal_approval_loan_documents_upload_documents_secondary',
    'formal_approval_loan_documents_finalised',
    'settlement',
    'settlement_upload_documents',
    'settlement_upload_documents_secondary'
  ].includes(currentSpecialistStepUid);
};


/*
export const copySvg = async (imgEl) => {
  imgEl.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  let svgData = imgEl.outerHTML;
  let preface = '<?xml version="1.0" standalone="no"?>\r\n';
  let svgBlob = new Blob([preface, svgData], {type:"image/svg+xml;charset=utf-8"});

  await navigator.clipboard.write([
    new window.ClipboardItem({'image/svg+xml': svgBlob}),
  ]);

  /!*navigator.clipboard.writeText(base64data);
  let reader = new FileReader();
  reader.readAsDataURL(svgBlob);
  reader.onloadend = function() {
    let base64data = reader.result;
    console.log('svgBlob:', base64data);
    navigator.clipboard.writeText(base64data);
  }*!/
  //console.log('svgBlob', svgBlob);
  //navigator.clipboard.writeText(await svgBlob.text());
}*/

export const convertAddressToDomiumAddress = (address) => {
  if (!address.fullAddress && !address.id) {
    return undefined;
  } else if (!!address.fullAddress) {
    return address;
  } else {
    return {
      fullAddress: address.address,
      premise: address.addressComponents?.unitNumber,
      number: address.addressComponents?.streetNumber,
      street: address.addressComponents?.streetName,
      locality: address.addressComponents?.suburb,
      postcode: address.addressComponents?.postCode,
      state: address.addressComponents?.state,
      unitNumber: address.addressComponents?.unitNumber,
      country: "Australia"
    };
  }
};

export const convertAddressToDomainAddress = (address, propertyId) => {
  if (!address.fullAddress) {
    return undefined;
  } else if (propertyId) {
    return {
      address: address.fullAddress,
      id: propertyId,
      addressComponents: {
        unitNumber: !!address?.unitNumber ? address.unitNumber : address.premise,
        streetNumber: address.number,
        streetName: address.street,
        suburb: address.locality,
        postCode: address.postcode,
        state: address.state,
      },
    };
  } else {
    return address;
  }
};
